//import "./safetyforms.css";
import moment from "moment";
import { useContext, useEffect, useReducer, useState } from "react";
import "react-dropdown/style.css";
import { useForm } from "react-hook-form";
import { FaChevronLeft, FaChevronRight, FaDownload, FaSpinner, FaTimesCircle, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { deleteDailyReport, exportDailyReportPDF, fetchDailyReportByCrewMemberId, fetchDailyReportByCustomerId, fetchDailyReports, findDailyReports, selectDailyReports, selectLoading, selectPagination } from "../common/slices/dailyreport";
import { resetPagination } from "../common/slices/globalActions";
import { selectActive } from "../common/slices/location";
import { ErrorContext } from "../ErrorContext";
import ActionContainer from "./ActionContainer";
import { BlackButton, ButtonBase, LightButton } from "./Buttons";
import FormFilterDropDown from "./FormFilterDropDown";
import Layout from "./Layout";
import ModalConfirm from "./ModalConfirm";
import { StyledSearch } from "./Search";
import { MenuItem, Table, TableData, TableDataLast, TableDataUnderline, TableFooter, TableHeader, TableMenu, TableRow } from "./Table";
import { H1 } from "./Typography";
import PreviewDailyReport from "./ScreenPreviewDailyReport";
import { UncontrolledCheckboxInput } from "./FormCheckbox";
import selectionReducer from "../utils/selectionReducer";

const getTypeName = (type) => {
    switch (type) {
        case "DAILY_REPORT":
            return "Daily Report";
        case "INSPECTION":
            return "Inspection";
        case "SAFETY":
            return "Safety Inspection";
    }
}

const ScreenListDailyReport = () => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const nav = useNavigate();
    const { id } = useParams();
    const [selection, dispatchSelection] = useReducer(selectionReducer, {});
    const [showConfirm, setShowConfirm] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const dailyReports = useSelector(selectDailyReports);
    const pagination = useSelector(selectPagination);
    const loading = useSelector(selectLoading);
    const locationId = useSelector(selectActive);

    const selectionCount = Object.values(selection).filter(e => e).length;
    const hasSelection = selectionCount > 0;
    const allSelected = dailyReports.length > 0 && dailyReports.every(item => selection[item.id]);

    const form = useForm({
        values: {
            draft: false,
            filterBy: { value: 'byLocation', label: "Location" },
            startDate: moment().add(-7, 'days').startOf('day').toDate(),
            endDate: moment().endOf('day').toDate(),
        }
    });

    const onSearch = async ({ search }) => {
        try {
            form.setValue('filterBy', { value: 'bySearch', label: 'Search' })
            form.setValue('search', search)
            await form.handleSubmit(onFilter)();
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to search documents.", forSupport: ex.message });
        }
    }

    const onReset = async () => {
        try {
            form.setValue('filterBy', { value: 'byLocation', label: 'Location' })
            await form.handleSubmit(onFilter)();
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to reset search", forSupport: ex.message });
        }
    }

    const dispatcher = {
        byLocation: (filterParams, nextToken, storeNextToken) => dispatch(fetchDailyReports(locationId, undefined, undefined, nextToken, storeNextToken)),
        byDate: (filterParams, nextToken, storeNextToken) => dispatch(fetchDailyReports(locationId, filterParams.startDate, filterParams.endDate, nextToken, storeNextToken)),
        byForeman: (filterParams, nextToken, storeNextToken) => dispatch(fetchDailyReportByCrewMemberId(filterParams.crewMemberId.value, filterParams.startDate, filterParams.endDate, 12, nextToken, storeNextToken)),
        byAuthor: (filterParams, nextToken, storeNextToken) => dispatch(fetchDailyReportByCrewMemberId(filterParams.crewMemberId.value, filterParams.startDate, filterParams.endDate, 12, nextToken, storeNextToken)),
        byCustomer: (filterParams, nextToken, storeNextToken) => dispatch(fetchDailyReportByCustomerId(filterParams.customerId.value, filterParams.startDate, filterParams.endDate, 12, nextToken, storeNextToken)),
        bySearch: (filterParams, nextToken, storeNextToken) => dispatch(findDailyReports(locationId, filterParams.search, undefined, nextToken, storeNextToken)),
    }

    useEffect(() => {
        if (locationId) {
            form.handleSubmit(onFilter)();
        }
    }, [form, locationId, dispatch]);

    const onDeleteClick = (e, report) => {
        e.stopPropagation();
        setShowConfirm(report);
    }

    const onDeleteConfirm = async () => {
        try {
            await dispatch(deleteDailyReport(showConfirm));
            setShowConfirm(false);
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to delete report", forSupport: ex.message });
        }
    }

    const onDownloadIndividual = async (e, report) => {
        try {
            e.stopPropagation();
            const url = await dispatch(exportDailyReportPDF({ dailyReportId: report.id }))
            window.open(url, "_blank");
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get signed url for report", forSupport: ex.message });
        }
    }

    const onDownloadClick = async (e) => {
        try {
            setDownloading(true);
            const selectedIds = Object.entries(selection).filter(([id, selected]) => selected)
            await Promise.all(selectedIds.map(async ([id, selected]) => {
                const url = await dispatch(exportDailyReportPDF({ dailyReportId: id }))
                window.open(url);
            }))
            setDownloading(false);
            dispatchSelection({ type: "RESET" })
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get signed url for report", forSupport: ex.message });
        }
    }

    const onReportClick = (d) => {
        nav(`/daily-report/${d.id}`);
    };

    const onFilter = async (filterParams) => {
        try {
            dispatch(resetPagination());
            if (filterParams?.filterBy?.value) {
                await dispatcher[filterParams?.filterBy?.value](filterParams);
            }
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get reports", forSupport: ex.message })
        }
    }

    const onNextPage = async (filterParams) => {
        try {
            await dispatcher[filterParams?.filterBy?.value](filterParams, pagination.nextToken);
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get next page of reports", forSupport: ex.message })
        }
    }

    const onPrevPage = async (filterParams) => {
        try {
            await dispatcher[filterParams?.filterBy?.value](filterParams, pagination.previousTokens[pagination.previousTokens.length - 2], false);
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get previous page of reports", forSupport: ex.message })
        }
    }

    return (
        <Layout>
            <H1 style={{ marginTop: '2rem' }}>Reports</H1>
            <ActionContainer>
                <div style={{ flex: 1 }} />
                <StyledSearch onSubmit={onSearch} onResetClick={onReset} />
                <FormFilterDropDown form={form} locationId={locationId} onFilter={onFilter} />
                {hasSelection && <BlackButton onClick={onDownloadClick} disabled={downloading}>Download ({selectionCount})</BlackButton>}
                {hasSelection && <LightButton onClick={() => dispatchSelection({ type: "RESET" })} disabled={downloading}>Cancel</LightButton>}
            </ActionContainer>
            <Table>
                <thead>
                    <tr>
                        <TableHeader>
                            <UncontrolledCheckboxInput
                                containerStyle={{ margin: 0, padding: 0 }}
                                style={{ transform: 'none', width: '1rem', height: '1rem', lineHeight: '1rem' }}
                                errors={{}}
                                value={allSelected}
                                onChange={(v) => {
                                    dispatchSelection({ type: "TOGGLE_ALL", payload: { ids: dailyReports.map(dr => dr.id), value: !allSelected } })
                                }}
                            />
                        </TableHeader>
                        <TableHeader style={{ width: '35%' }}>Job Name</TableHeader>
                        <TableHeader style={{ width: '15%' }}>Type</TableHeader>
                        <TableHeader style={{ width: '25%' }}>Date submitted</TableHeader>
                        <TableHeader style={{ width: '25%' }}>Completed By</TableHeader>
                    </tr>
                </thead>
                <tbody>
                    {dailyReports.map((report) => (
                        <TableRow key={report.id} onClick={() => onReportClick(report)}>
                            <TableData onClick={e => e.stopPropagation()} style={{ padding: 0, textAlign: 'center' }}>
                                <UncontrolledCheckboxInput
                                    containerStyle={{ margin: 0, padding: 0 }}
                                    style={{ transform: 'none', width: '1rem', height: '1rem', lineHeight: '1rem' }}
                                    errors={{}}
                                    value={selection[report.id]}
                                    onChange={(v) => {
                                        dispatchSelection({ type: "TOGGLE", payload: report.id })
                                    }}
                                />
                            </TableData>
                            <TableDataUnderline>{report.customer?.name ?? report.customerName}</TableDataUnderline>
                            <TableData>{getTypeName(report.type)}</TableData>
                            <TableData>{moment(report.submissionDate).format("MMM D, YYYY [at] h:mm A")}</TableData>
                            <TableDataLast>
                                {report?.foreman?.active ? '' : <FaTimesCircle title="Inactive User" />}&nbsp;
                                {`${report?.foreman?.name}`}
                                <TableMenu>
                                    <MenuItem onClick={(e) => onDeleteClick(e, report)}>Delete <FaTrash style={{ marginLeft: "auto" }} /></MenuItem>
                                    <MenuItem onClick={(e) => onDownloadIndividual(e, report)} disabled={!report?.pdfKey}>Download <FaDownload style={{ marginLeft: "auto" }} /></MenuItem>
                                </TableMenu>
                            </TableDataLast>
                        </TableRow>
                    ))}
                    {
                        !loading && !dailyReports.length && <TableRow><TableData colSpan={4}>No reports, please adjust your filter</TableData></TableRow>
                    }
                </tbody>
            </Table>
            <TableFooter>
                {loading && <FaSpinner className='fa-spin' />}
                <ButtonBase onClick={form.handleSubmit(onPrevPage)} disabled={!pagination.previousTokens[pagination.previousTokens.length - 1] || loading} style={{ color: 'black' }}><FaChevronLeft /></ButtonBase>
                <ButtonBase onClick={form.handleSubmit(onNextPage)} disabled={!pagination.nextToken || loading} style={{ color: 'black' }}><FaChevronRight /></ButtonBase>
            </TableFooter>

            {
                id && <PreviewDailyReport id={id} />
            }

            {showConfirm &&
                <ModalConfirm
                    title="Delete Daily Report?"
                    description={`Are you sure you want to delete the daily report for ${showConfirm.customer?.name} from ${moment(showConfirm.submissionDate).format('MMMM DD, YYYY [at] h:mm A')} by ${showConfirm.foreman?.name}?  This operation is permanent and cannot be undone.`}
                    onClose={() => setShowConfirm(false)}
                    onConfirm={onDeleteConfirm}
                />
            }
        </Layout>
    );
};
export default ScreenListDailyReport;
